import React, { useCallback, useMemo } from "react";
import Link from "next/link";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Paper, Tooltip } from "@material-ui/core";
import {
  VpnKey as ChangePasswordIcon,
  Edit as EditIcon
} from "@material-ui/icons";
import { ActiveStatus } from "src/apps/commons/dataDisplays";
import { DataTable } from "src/apps/commons/dataTable";
import { formatDateTime } from "src/utils";
import * as Api from "../api";
import {
  IAdminUser as Entity,
  IAdminUserPaginateReq as PaginateReq,
  AdminUserPaginateOptionKey as OptionKey
} from "../types";
import { constants, paths } from "../utils";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  }
}));

const ListContainers: React.FC<unknown> = _ => {
  const classes = useStyles();

  const ActiveCell = useCallback(
    (data: Entity) => <ActiveStatus value={data.active} />,
    []
  );

  const ManageCell = useCallback(
    (data: Entity, isLoading: boolean) => (
      <div>
        {isLoading ? (
          <IconButton
            aria-label="Edit"
            key={`loading_edit_${data.id}`}
            disabled
          >
            <EditIcon />
          </IconButton>
        ) : (
          <Link href={`${paths.edit}?id=${data.id}`} key={`edit_${data.id}`}>
            <a>
              <IconButton aria-label="Edit">
                <Tooltip title={`แก้ไข ${constants.label}`} placement="top">
                  <EditIcon />
                </Tooltip>
              </IconButton>
            </a>
          </Link>
        )}

        {isLoading ? (
          <IconButton
            aria-label="Change Password"
            key={`loading_manage_${data.id}`}
            disabled
          >
            <ChangePasswordIcon />
          </IconButton>
        ) : (
          <Link
            href={`${paths.changePassword}?id=${data.id}`}
            key={`changePassword_${data.id}`}
          >
            <a>
              <IconButton aria-label="Change Password">
                <Tooltip title={`เปลี่ยน Password`} placement="top">
                  <ChangePasswordIcon />
                </Tooltip>
              </IconButton>
            </a>
          </Link>
        )}
      </div>
    ),
    []
  );

  const defaultCriteria = useMemo(
    () =>
      ({
        paginate: {
          page: 1,
          pageSize: 10,
          order: { optionKey: "USERNAME", desc: false }
        }
      } as PaginateReq),
    []
  );

  return (
    <Paper className={classes.root}>
      <DataTable<Entity, PaginateReq, OptionKey, "MANAGE">
        searchTermLabel={"ค้นหาจาก Username/ชื่อ"}
        listEntity={Api.list}
        pathCreate={paths.create}
        defaultCriteria={defaultCriteria}
        columns={[
          {
            id: "MANAGE",
            label: "จัดการ",
            width: 120,
            minWidth: 120,
            style: { paddingLeft: 8, paddingRight: 8 },
            align: "center",
            renderCell: ManageCell
          },
          {
            id: "USERNAME",
            label: "Username",
            minWidth: 150,
            orderable: true,
            renderCell: data => data.username
          },
          {
            id: "FULL_NAME",
            label: "ชื่อ",
            minWidth: 200,
            orderable: true,
            renderCell: data => data.fullName
          },
          {
            id: "EMAIL",
            label: "Email",
            minWidth: 200,
            orderable: true,
            renderCell: data => data.email ?? ""
          },
          {
            id: "ACTIVE",
            label: "Active",
            width: 80,
            minWidth: 80,
            orderable: true,
            align: "center",
            renderCell: ActiveCell
          },
          {
            id: "CREATED_AT",
            label: "สร้างเมื่อ",
            width: 200,
            minWidth: 200,
            orderable: true,
            renderCell: data => formatDateTime(data.createdAt)
          },
          {
            id: "UPDATED_AT",
            label: "แก้ไขล่าสุดเมื่อ",
            width: 200,
            minWidth: 200,
            orderable: true,
            renderCell: data => formatDateTime(data.updatedAt)
          }
        ]}
      />
    </Paper>
  );
};

export default ListContainers;
